import { useQueryClient } from '@tanstack/react-query';
import { assert } from 'assert-ts';
import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { hostQueryKeys } from '#api';
import { AddIcon, BlockIcon, CheckCircle, LockIcon, MobileIcon } from '#assets/ReactSvg.ts';
import { LinkButton } from '#components/atoms/buttons/link-button/LinkButton.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { Pill } from '#components/atoms/pill/Pill.tsx';
import { SKIPPED_GET_STARTED_KEY } from '#components/patterns/routing/GotStarted.tsx';
import type { OSGroup } from '#dn-types/downloads.ts';
import type { Network } from '#dn-types/network.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useBrowserStorage } from '#hooks/useBrowserStorage.ts';
import { useTimeout } from '#hooks/useTimeout.ts';
import { generateHostNames } from '#util/generateHostName.ts';
import { DefineNetwork } from './DefineNetwork.tsx';
import { useCreateGetStartedHostMutation, useGetStartedHostStatus, usePollHostEnrolled } from './GetStarted.queries.ts';
import { HostCard } from './HostCard.tsx';
import { InstallSection } from './InstallSection.tsx';
import { PendingHostCard } from './PendingHostCard.tsx';
import { osGroupFromMetadata } from './ping-command.ts';
import { PingTestSection } from './PingTestSection.tsx';
import { Waiting } from './Waiting.tsx';
import styles from './GetStarted.module.css';

const TOTAL_STEPS = 4;

// How long to dwell on a host's "connected!" success state before advancing the step — a UI beat,
// long enough to register the win (mirrors the enroll page's post-enrollment grace pause).
const CONNECTED_BEAT_MS = 2000;

const INSTALL_SUBTEXT =
  'Run this on the machine you want to add to your network. It installs the dnclient and enrolls the host.';

// Step 0 defines the network; 1–3 are the host-connection steps.
type Step = 0 | 1 | 2 | 3;

/**
 * A single, persistent page: as hosts "connect", sections are added/updated in
 * place rather than swapping the whole screen. Shared sections (the progress
 * header, the install command, the first host card) stay mounted across steps,
 * and only newly-revealed sections animate in via the `appear` class.
 *
 * Step 0 creates a real network; entering steps 1 and 2 creates a real host +
 * enrollment code (a per-host ICMP role, then the host itself). Connection is
 * real too: each host's enrollment code is polled until a dnclient consumes it,
 * which advances the step (no demo button).
 */
export function GetStarted() {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>(0);
  const [network, setNetwork] = useState<Network | null>(null);
  // Generated once per session so the two hosts get distinct, whimsical names (e.g. eager-falcon).
  const [hostNames] = useState(() => generateHostNames(2));
  const [firstName = 'first-host', secondName = 'second-host'] = hostNames;
  // The install picker's OS choice, lifted from InstallSection (one instance persists across both
  // host steps).
  const [installOS, setInstallOS] = useState<OSGroup>('Linux');

  const firstHostMutation = useCreateGetStartedHostMutation();
  const secondHostMutation = useCreateGetStartedHostMutation();
  // The mutation returns the create-time snapshot; the live-status hooks below re-fetch each host to
  // show its real status (the snapshot has no lastSeenAt yet). The IP is assigned at create, so it's
  // stable in the snapshot — only the status needs the live poll.
  const firstHost = firstHostMutation.data?.host;
  const secondHost = secondHostMutation.data?.host;
  // One live-status observer per host, owned here: the cards, the ping section, and the phone card
  // below all read these two results. React Query runs refetchInterval per observer, so a second
  // observer per host would double the request rate and burn the shared poll budget in half the
  // wall time. Gated to the steps that show a status (the host panel appears at step 2) so the poll
  // budget isn't spent while a host is still enrolling.
  const firstLive = useGetStartedHostStatus(step >= 2 ? (firstHost ?? null) : null);
  const secondLive = useGetStartedHostStatus(step >= 2 ? (secondHost ?? null) : null);

  const createFirstHost = () => {
    const net = assert(network, 'network must exist before creating a host');
    firstHostMutation.mutate({ networkID: net.id, name: firstName });
  };
  const createSecondHost = () => {
    const net = assert(network, 'network must exist before creating a host');
    secondHostMutation.mutate({ networkID: net.id, name: secondName });
  };

  // Step 0 → 1: capture the created network, then kick off the first host's creation.
  const handleNetworkCreated = (created: Network) => {
    setNetwork(created);
    firstHostMutation.mutate({ networkID: created.id, name: firstName });
    setStep(1);
  };

  // Real connection detection replaces the old "Next step (demo)" button: poll each host's enrollment
  // code until a dnclient consumes it. `enabled` gates each poll to its active step so neither runs
  // before its host exists.
  const firstConnected = usePollHostEnrolled(firstHost?.id ?? '', firstHostMutation.data?.enrollmentCode.code ?? '', {
    enabled: step === 1,
  });
  const secondConnected = usePollHostEnrolled(
    secondHost?.id ?? '',
    secondHostMutation.data?.enrollmentCode.code ?? '',
    {
      enabled: step === 2,
    }
  );

  // Connection drives the flow forward: dwell on the "connected!" success state for a beat, then
  // advance. Leaving step 1 also creates the second host so its enroll command is ready.
  useAdvanceAfterConnectedBeat(firstConnected, () => {
    if (secondHostMutation.isIdle) {
      createSecondHost();
    }
    setStep(2);
  });
  useAdvanceAfterConnectedBeat(secondConnected, () => {
    // The enroll that consumed the code also rewrote this host's metadata server-side (os,
    // lastSeenAt) — re-read the detail now rather than waiting on the next poll tick, whose budget
    // may already be spent after a long enroll wait.
    if (secondHost) {
      void queryClient.invalidateQueries({ queryKey: hostQueryKeys.detail(secondHost.id) });
    }
    setStep(3);
  });

  // During the beat (connected, step not yet advanced) the waiting indicator shows its success state
  // before the next section reveals.
  const activeConnected = step === 1 ? firstConnected : secondConnected;

  // Step 0: define the network before the host-connection flow.
  if (step === 0) {
    return (
      <div className={styles.GetStarted}>
        <ProgressHeader step={1} />
        <DefineNetwork onCreate={handleNetworkCreated} />
        <FlowFooter exit="skip" />
      </div>
    );
  }

  const hasFirstHost = step >= 2;
  const hasSecondHost = step >= 3;
  const collecting = step < 3;
  const done = step === 3;
  // One of the enrolled hosts is a phone (per its detected OS, read from the same live results the
  // cards render): the "Set up your phone" card acknowledges the step is already done instead of
  // pitching it.
  const phoneEnrolled =
    osGroupFromMetadata(firstLive?.host.metadata.os) === 'Mobile' ||
    osGroupFromMetadata(secondLive?.host.metadata.os) === 'Mobile';

  return (
    <div className={styles.GetStarted}>
      {/* Header: the progress bar fills in place across every step, including the
          final "Network ready" reward (step 4 of 4, all segments filled). step + 1
          because the network step (internal 0) is step 1 of 4. */}
      {collecting ?
        <ProgressHeader key="header" step={step + 1} />
      : <>
          <ProgressHeader key="header" step={TOTAL_STEPS} />
          <div key="banner" className={styles.GetStarted_appear}>
            <StatusBanner title="Network ready" />
          </div>
        </>
      }

      {/* Headline above the host panel — text updates in place between steps 2 and 3 */}
      {hasFirstHost ?
        <div key="headline" className={styles.GetStarted_appear}>
          {done ?
            <>
              <h1 className="h2 mb-8">You now have two hosts, talking directly</h1>
              <p className={`p-md color-lowlight ${styles.GetStarted_textMd}`}>
                These machines can reach each other peer-to-peer, encrypted end-to-end.
              </p>
            </>
          : <h1 className="h2">First host connected</h1>}
        </div>
      : null}

      {/* The two-host shape shows as soon as the first host connects: the second
          slot and the link stay grayed/pending, then fill in once it connects. */}
      {hasFirstHost ?
        <div key="hosts" className={`${styles.GetStarted_hostPanel} ${styles.GetStarted_appear}`}>
          <div className={styles.GetStarted_hostPair}>
            {firstLive ?
              <HostCard host={firstLive.host} status={firstLive.status} />
            : <PendingHostCard />}
            <div
              className={
                hasSecondHost ?
                  styles.GetStarted_hostLink
                : `${styles.GetStarted_hostLink} ${styles.GetStarted_hostLink___pending}`
              }
              aria-hidden={true}
            >
              <div className={styles.GetStarted_hostLinkTrack}>
                {hasSecondHost ?
                  <span className={styles.GetStarted_hostLinkPulse} />
                : null}
              </div>
              {hasSecondHost ?
                <span className={styles.GetStarted_hostLinkChip}>
                  <LockIcon aria-hidden={true} width={12} height={12} />
                </span>
              : null}
            </div>
            {secondLive ?
              <HostCard host={secondLive.host} status={secondLive.status} className={styles.GetStarted_appear} />
            : <PendingHostCard />}
          </div>
        </div>
      : null}

      {/* "Add a host" block persists across steps 1–2: the install command and the
          user's OS choice stay put while the heading and waiting label update. */}
      {collecting ?
        <div key="add" className={styles.GetStarted_addHost}>
          <div>
            {step === 1 ?
              <h1 className="h2 mb-8">Add your first host</h1>
            : <h3 className="h3 mb-8">Add a second host</h3>}
            <p className={`p-md color-lowlight mb-0 ${styles.GetStarted_textMd}`}>{INSTALL_SUBTEXT}</p>
          </div>
          <InstallSection
            creation={step === 1 ? firstHostMutation : secondHostMutation}
            onRetry={step === 1 ? createFirstHost : createSecondHost}
            os={installOS}
            onOSChange={setInstallOS}
          />
          <Waiting
            connected={activeConnected}
            label={
              activeConnected ?
                step === 1 ?
                  'First host connected'
                : 'Second host connected'
              : step === 1 ?
                'Waiting for first host to connect'
              : 'Waiting for second host to connect'
            }
          />
        </div>
      : null}

      {/* Final-step sections animate in once the network is up. The ping section reads each live
          host's detected OS from the hoisted results — written by the enroll request itself, so
          it's present by the time this step can render. */}
      {done && firstLive && secondLive ?
        <div key="tryit" className={styles.GetStarted_appear}>
          <h3 className="h3 mb-8">Try it out</h3>
          <p className={`p-md color-lowlight mb-16 ${styles.GetStarted_textMd}`}>
            Ping one host from the other — you should see replies quickly.
          </p>
          <PingTestSection hosts={[firstLive, secondLive]} />
        </div>
      : null}

      {/* Same-tab links are safe here: the flow is complete, and GetStartedPage's RevisitGate
          redirects any return visit to the admin panel instead of remounting the flow. */}
      {done ?
        <div key="cards" className={`${styles.GetStarted_infoCards} ${styles.GetStarted_appear}`}>
          <Link to="/hosts/add" className={styles.GetStarted_infoCard}>
            <div className={styles.GetStarted_infoCardTitle}>
              <MobileIcon aria-hidden={true} width={16} height={16} className="color-accent" />
              Set up your phone
              {phoneEnrolled ?
                <CheckCircle aria-hidden={true} width={16} height={16} className="color-success ml-auto shrink-0" />
              : <Pill className="ml-auto shrink-0">2 min</Pill>}
            </div>
            <p className={styles.GetStarted_infoCardDesc}>
              {phoneEnrolled ?
                'Done — your phone is already on this network. Add another device any time.'
              : 'Install the mobile app and scan a QR code to reach your network from your phone, at home or on the go.'
              }
            </p>
          </Link>
          <Link to="/roles" className={styles.GetStarted_infoCard}>
            <div className={styles.GetStarted_infoCardTitle}>
              <BlockIcon aria-hidden={true} width={16} height={16} className="color-accent" />
              Set firewall rules
            </div>
            <p className={styles.GetStarted_infoCardDesc}>
              Right now all traffic except ping between your hosts is blocked. Allow traffic with roles and tags.
            </p>
          </Link>
          <Link to="/hosts/add" className={styles.GetStarted_infoCard}>
            <div className={styles.GetStarted_infoCardTitle}>
              <AddIcon aria-hidden={true} width={16} height={16} className="color-accent" />
              Add more hosts
            </div>
            <p className={styles.GetStarted_infoCardDesc}>
              Up to 100 on the free tier. Phones, servers, containers — they all join the same way.
            </p>
          </Link>
        </div>
      : null}

      {/* Footer: docs plus an escape hatch (talk to us, or skip out of the flow) so a user who's stuck
          enrolling — or who already knows the ropes — is never trapped. On the final step the exit
          zone graduates to the "all set up" reward with "Go to the admin panel". */}
      <FlowFooter key="footer" exit={done ? 'done' : 'skip'} />
    </div>
  );
}

// Run `advance` once, CONNECTED_BEAT_MS after `connected` first flips true — the dwell-then-advance
// beat that lets the success state register before the next section reveals.
function useAdvanceAfterConnectedBeat(connected: boolean, advance: () => void) {
  const { start } = useTimeout(advance, CONNECTED_BEAT_MS);
  const startedRef = useRef(false);
  useEffect(() => {
    if (connected && !startedRef.current) {
      startedRef.current = true;
      start();
    }
  }, [connected, start]);
}

// Steps 1–2 show progress toward the goal; the final step swaps this for the
// pulsing "Network ready" banner so it lands as a reward, not a constant.
function ProgressHeader({ step }: { step: number }) {
  return (
    <div className={styles.GetStarted_progress}>
      <div className={styles.GetStarted_progressLabel}>
        Step {step} of {TOTAL_STEPS}
      </div>
      <div className={styles.GetStarted_progressTrack}>
        {Array.from({ length: TOTAL_STEPS }, (_, i) => (
          <span
            key={i}
            className={
              i < step ?
                `${styles.GetStarted_progressSegment} ${styles.GetStarted_progressSegment___filled}`
              : styles.GetStarted_progressSegment
            }
          />
        ))}
      </div>
    </div>
  );
}

function StatusBanner({ title }: { title: string }) {
  return (
    <div className={styles.GetStarted_statusBanner}>
      <CheckCircle aria-hidden={true} width={22} height={22} className="color-success shrink-0" />
      <div className={styles.GetStarted_statusBannerTitle}>{title}</div>
    </div>
  );
}

// Two-zone footer under every step — divider, then the exit affordance (how you leave the flow),
// then the persistent help links. Before the done step the exit is a centered whisper; skipping
// marks the session so the GotStarted gate lets a network-less user through, and returns them to
// the admin route that bounced them here, if any. Otherwise both exits target `/` so they track
// whatever the app's index route resolves to (today: /hosts).
function FlowFooter({ exit }: { exit: 'skip' | 'done' }) {
  const from = useAppLocation().state?.from;
  const [, setSkippedGetStarted] = useBrowserStorage(SKIPPED_GET_STARTED_KEY);

  return (
    <div className={styles.GetStarted_docsLinks}>
      <hr className={styles.GetStarted_divider} />
      <div className={styles.GetStarted_footerRow}>
        <div className={styles.GetStarted_links}>
          <a className="a" href="https://www.defined.net/docs/" target="_blank" rel="noreferrer">
            View docs
          </a>
          <span aria-hidden={true}>|</span>
          {/* New tab on purpose: the flow's progress lives in component state, so a same-tab
              navigation would discard it. */}
          <Link className="a" to="/support/contact" target="_blank" rel="noreferrer">
            Talk to us
          </Link>
        </div>
        {exit === 'skip' ?
          <div className={styles.GetStarted_footerExit}>
            <span>
              Already know how Managed Nebula works?{' '}
              <Link className="a" to={from ?? '/'} onClick={() => setSkippedGetStarted('true')}>
                Skip to admin panel
              </Link>
            </span>
          </div>
        : null}
        {exit === 'done' ?
          <div className={cn(styles.GetStarted_footerExit, styles.GetStarted_appear)}>
            <span>You’re all set up. This page won’t show again.</span>
            <LinkButton to="/">Go to the admin panel</LinkButton>
          </div>
        : null}
      </div>
    </div>
  );
}
